import { ANALYTICS_CATEGORIES, categoryOfValue, categoryValues } from '@/analytics/analyticsCategories'
import { DISABLED_DASHBOARD_VALUES } from '@/analytics/disabledDashboards'
import { DASHBOARD_FEATURE } from '@/analytics/dashboardFeatureMap'

/**
 * The analytics report registry: every dashboard the portal can render, keyed
 * by `value`. Lives here rather than in pages/Dashboard.tsx so the shell, the
 * category header, the sidebar, and the subscription/report-builder pickers can
 * all read it without importing the shell (which imports the header back).
 *
 * `scope` prefixes building reports with /b/:bId and `portfolioScope` the
 * portfolio reports with /p/:gId; each kind ignores the other's prefix.
 */
export const categoryRank = (value: string) => {
  const index = ANALYTICS_CATEGORIES.findIndex((category) => categoryValues(category).includes(value))
  return index === -1 ? ANALYTICS_CATEGORIES.length : index
}

export const getTableList = (scope = '', portfolioScope = '') =>
  [
    {
      label: 'Engineering',
      value: 'Engineering',
      path: `${scope}/analytics/dashboard/roundAnalytics/Engineering`,
      urlDateRange: true,
      dateRangeInline: true,
      emailInline: true,
      emailDisabled: false,
      puppeteerUrl: 'pdf/Engineering',
      reportBuilder: true,
      permissionKey: 'EngineeringRounds',
    },
    {
      label: 'AED Rounds',
      value: 'AEDInspection',
      path: `${scope}/analytics/dashboard/roundAnalytics/AEDInspection`,
      urlDateRange: true,
      dateRangeInline: true,
      emailInline: true,
      emailDisabled: false,
      permissionKey: 'AEDRounds',
    },
    {
      label: 'Fire Extinguisher Rounds',
      value: 'FireExtinguisherInspection',
      path: `${scope}/analytics/dashboard/roundAnalytics/FireExtinguisherInspection`,
      urlDateRange: true,
      dateRangeInline: true,
      emailInline: true,
      emailDisabled: false,
      permissionKey: 'FireExtinguisherRounds',
    },
    {
      label: 'Inspection Details',
      value: 'InspectionRound',
      path: `${scope}/analytics/dashboard/roundAnalytics/Inspection`,
      urlDateRange: true,
      dateRangeInline: true,
      emailInline: true,
      emailDisabled: false,
      reportBuilder: true,
      permissionKey: 'InspectionRounds',
    },
    {
      label: 'Cleaning Details',
      value: 'Cleaning',
      path: `${scope}/analytics/dashboard/roundAnalytics/Cleaning`,
      urlDateRange: true,
      dateRangeInline: true,
      emailInline: true,
      emailDisabled: false,
      reportBuilder: true,
      permissionKey: 'CleaningRounds',
    },
    {
      // Its own report rather than a button on Cleaning Details. No urlDateRange:
      // the page owns its period selector (week/month/quarter/year) and keys its
      // stored report off that, so the shell's range picker would not apply.
      label: 'Cleaning Intelligence',
      value: 'CleaningIntelligence',
      path: `${scope}/analytics/dashboard/cleaningIQ`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'CleaningRounds',
    },
    {
      // Engineering IQ building report (specs#3482): reviews one building's engineering
      // rounds for the period. Owns its own period selector, so no urlDateRange.
      label: 'Engineering Intelligence',
      value: 'EngineeringIntelligence',
      path: `${scope}/analytics/dashboard/engineeringIQ`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'EngineeringRounds',
    },
    {
      // Security IQ building report (specs#3482): reviews one building's security patrols
      // and incidents for the period. Owns its own period selector, so no urlDateRange.
      label: 'Security Intelligence',
      value: 'SecurityIntelligence',
      path: `${scope}/analytics/dashboard/securityIQ`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'SecurityPatrols',
    },
    {
      label: 'Equipment Collection',
      value: 'EquipmentDetailCollection',
      path: `${scope}/analytics/dashboard/roundAnalytics/EquipDetailCollectRounds`,
      urlDateRange: true,
      dateRangeInline: true,
      emailInline: true,
      permissionKey: 'EquipmentDetailsCollectionRound',
    },
    {
      label: 'Security Patrols',
      value: 'SecurityPatrol',
      path: `${scope}/analytics/dashboard/roundAnalytics/SecurityPatrol`,
      urlDateRange: true,
      dateRangeInline: true,
      emailInline: true,
      emailDisabled: false,
      reportBuilder: true,
      permissionKey: 'SecurityPatrols',
    },
    {
      label: 'Preventative Maintenance',
      value: 'PreventativeMaintenance',
      path: `${scope}/analytics/dashboard/preventativeMaintenance`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'PreventativeMaintenance',
      hidden: true, // suppressed from the dashboard dropdown (route still reachable directly)
    },
    {
      // Renamed from "PM Dashboard" (nav reorg); the hidden legacy
      // 'PreventativeMaintenance' entry above keeps the old page's name.
      label: 'Preventative Maintenance',
      value: 'PMCompliance',
      hideTitle: true,
      path: `${scope}/analytics/dashboard/pmCompliance`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'PreventativeMaintenance',
    },
    {
      label: 'PM Library Dashboard',
      value: 'PMLibraryDashboard',
      path: `${scope}/analytics/dashboard/pMLibraryDashboard`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'PMLibraryDashboard',
      hidden: true, // suppressed from the dashboard dropdown (route still reachable directly)
    },
    {
      label: 'Inspection Overview',
      value: 'Inspection',
      path: `${scope}/analytics/dashboard/inspectionReport`,
      emailDisabled: false,
      // No puppeteer render for the matrix; the PDF modal has its own Email action.
      showEmail: false,
      permissionKey: 'InspectionDashboard',
    },
    {
      label: 'Supplier SLA Report',
      value: 'SupplierSLAReport',
      path: `${scope}/analytics/dashboard/supplierSLAReport`,
      // The dashboard draws EMAIL at the end of its own filter row (2026 kit restyle),
      // so the shell must not draw a second one above the page.
      emailInline: true,
      emailDisabled: false,
      permissionKey: 'SupplierSLAReport',
    },
    {
      label: 'Location Feedback',
      value: 'FeedbackDashboard',
      emailInline: true,
      path: `${scope}/analytics/dashboard/reportAnalyticsFeedback/Location`,
      emailDisabled: false,
      emailSendUrl: true,
      permissionKey: 'FeedbackDashboard',
    },
    {
      label: 'Custom Feedback Dashboard',
      value: 'CustomFeedbackDashboard',
      path: `${scope}/analytics/dashboard/customFeedbackDashboard`,
      showEmail: false,
      permissionKey: 'CustomFeedbackDashboard',
    },
    {
      label: 'Bathroom Service',
      value: 'BathroomService',
      path: `${scope}/analytics/dashboard/bathroomService`,
      showEmail: false,
      reportBuilder: true,
      permissionKey: 'BathroomService',
    },
    {
      label: 'Tenant Performance Dashboard',
      value: 'TenantPerformanceDashboard',
      emailInline: true,
      path: `${scope}/analytics/dashboard/tenantExperience/1`,
      emailDisabled: false,
      emailSendUrl: true,
      reportBuilder: true,
      permissionKey: 'TenantPerformanceDashboard',
    },
    {
      label: 'Tenant Health Dashboard',
      value: 'TenantHealthDashboard',
      emailInline: true,
      path: `${scope}/analytics/dashboard/tenantExperience/2`,
      emailDisabled: false,
      emailSendUrl: true,
      reportBuilder: true,
      permissionKey: 'TenantHealthDashboard',
    },
    {
      label: 'Supplier Check In Report',
      value: 'SupplierCheckInReport',
      path: `${scope}/analytics/dashboard/supplierCheckInReport`,
      // The dashboard draws EMAIL at the end of its own filter row (2026 kit restyle),
      // so the shell must not draw a second one above the page.
      emailInline: true,
      emailDisabled: false,
      reportBuilder: true,
      permissionKey: 'SupplierCheckInReport',
    },
    {
      label: 'Roof Access Authorization Report',
      value: 'RoofAccessReport',
      path: `${scope}/analytics/dashboard/roofAccessAuthorizationReport`,
      // Same screen as Supplier Check In, so EMAIL sits on its filter row too.
      emailInline: true,
      emailDisabled: false,
      reportBuilder: true,
      permissionKey: 'RoofAccessAuthorizationReport',
    },
    {
      label: 'Tenant HVAC Authorization Report',
      value: 'TenantHVACReport',
      path: `${scope}/analytics/dashboard/tenantHVACReport`,
      // Same screen as Supplier Check In, so EMAIL sits on its filter row too.
      emailInline: true,
      emailDisabled: false,
      permissionKey: 'TenantHVACAuthorizationReport',
    },
    {
      label: 'Feedback Dashboard',
      value: 'GlobalFeedbackDashboard',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalReport`,
      emailDisabled: false,
      emailSendUrl: true,
      permissionKey: 'FeedbackDashboard',
    },
    {
      label: 'Tenant Performance Dashboard',
      value: 'GlobalTenantPerformanceDashboard',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalTenantExperience/1`,
      emailDisabled: false,
      emailSendUrl: true,
      permissionKey: 'TenantPerformanceDashboard',
    },
    {
      label: 'Tenant Health Dashboard',
      value: 'GlobalTenantHealthDashboard',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalTenantExperience/2`,
      emailDisabled: false,
      emailSendUrl: true,
      permissionKey: 'TenantHealthDashboard',
    },
    {
      label: 'Inspection Overview',
      value: 'GlobalInspectionReport',
      hideTitle: true,
      // The dashboard draws EMAIL in its own filter row; the shell must not draw a second one.
      emailInline: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalInspectionReport`,
      emailDisabled: true,
      permissionKey: 'InspectionDashboard',
    },
    {
      label: 'Property Characteristics Index',
      value: 'GlobalPropertyCharacteristics',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalPropertyCharacteristicsIndex`,
      showEmail: false,
    },
    {
      label: 'Insurance Dashboard',
      value: 'GlobalInsuranceDashboard',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalInsuranceDashboard`,
      showEmail: false,
      permissionKey: 'InsuranceDashboard',
    },
    {
      label: 'Lease Intelligence',
      value: 'GlobalLeaseAbstraction',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalLeaseAbstraction`,
      showEmail: false,
    },
    {
      label: 'Roof Intelligence',
      value: 'GlobalRoofAbstraction',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalRoofAbstraction`,
      showEmail: false,
    },
    {
      label: 'Compliance Dashboard',
      value: 'GlobalComplianceRecords',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalComplianceRecords`,
      showEmail: false,
    },
    {
      label: 'Social Reputation',
      value: 'GlobalSocialReputation',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalGoogleRatingReport`,
      showEmail: false,
    },
    {
      label: 'Upsell Matrix',
      value: 'GlobalUpsellMatrix',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalUpsellMatrix`,
      showEmail: false,
    },
    {
      label: 'Cleaning Details',
      value: 'GlobalPortfolioRoundAnalytics',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalPortfolioRoundAnalytics/Cleaning`,
      showEmail: false,
    },
    {
      // Portfolio twin of the building-scoped Cleaning Intelligence report. No
      // urlDateRange: the page owns its period selector.
      label: 'Cleaning Intelligence',
      value: 'GlobalCleaningIQ',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalCleaningIQ`,
      showEmail: false,
    },
    {
      // Portfolio twin of Engineering Intelligence (specs#3482): benchmarks every
      // building on engineering round completion. Owns its own period selector.
      label: 'Engineering Intelligence',
      value: 'GlobalEngineeringIQ',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalEngineeringIQ`,
      showEmail: false,
    },
    {
      // Portfolio twin of Security Intelligence (specs#3482): benchmarks every building on
      // security patrols. Owns its own period selector.
      label: 'Security Intelligence',
      value: 'GlobalSecurityIQ',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalSecurityIQ`,
      showEmail: false,
    },
    {
      label: 'Security Rounds',
      value: 'GlobalPortfolioRoundAnalyticsSecurity',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalPortfolioRoundAnalytics/Security`,
      showEmail: false,
    },
    {
      label: 'Engineering',
      value: 'GlobalPortfolioRoundAnalyticsEngineering',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalPortfolioRoundAnalytics/Engineering`,
      showEmail: false,
    },
    {
      label: 'Mobile Engineering Report',
      value: 'GlobalPortfolioMobileEmployee',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalMobileEmployee`,
      puppeteerUrl: 'pdfMobileEmployee',
    },
    {
      // Portfolio twin of the building-scoped Equipment Data Integrity report.
      // A roll-up of the same per-building counts; no separate permission, so
      // it follows the building report's key.
      label: 'Equipment Data Integrity',
      value: 'GlobalEquipmentDataIntegrity',
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalDataIntegrityDashboard`,
      hideTitle: true,
      showEmail: false,
      permissionKey: 'EquipmentDataIntegrityDashboard',
    },
    {
      showEmail: false,
      label: 'Jobs Report',
      value: 'GlobalJobReport',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalJobReport`,
      puppeteerUrl: 'pdfJobReport',
    },
    {
      showEmail: false,
      label: 'Resident Feedback Report',
      value: 'GlobalResidentFeedback',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalResidentFeedback`,
    },
    {
      label: 'Social Reputation Report',
      value: 'SocialReputationReport',
      path: `${scope}/analytics/dashboard/residentFeedback`,
      showEmail: false,
      permissionKey: 'SocialReputationReport',
    },
    {
      label: 'Vacancy Lead',
      value: 'Vacancy',
      path: `${scope}/analytics/dashboard/vacancyLead`,
      emailDisabled: false,
      permissionKey: 'VacancyLead',
    },
    {
      label: 'Lead Capture',
      value: 'LeadCapture',
      path: `${scope}/analytics/dashboard/leadCapture/LeadCapture`,
      emailDisabled: false,
      permissionKey: 'LeadCapture',
    },
    {
      label: 'EV Request Report',
      value: 'EVRequest',
      path: `${scope}/analytics/dashboard/leadCapture/EVRequest`,
      emailDisabled: false,
      permissionKey: 'EVRequestReport',
    },
    {
      label: 'Room Rental',
      value: 'RoomRental',
      path: `${scope}/analytics/dashboard/leadCapture/RoomRental`,
      emailDisabled: false,
      permissionKey: 'RoomRental',
    },
    {
      label: 'Field Marketing',
      value: 'FieldMarketing',
      path: `${scope}/analytics/dashboard/leadCapture/FieldMarketing`,
      emailDisabled: false,
      permissionKey: 'FieldMarketing',
    },
    {
      label: 'Registrant List',
      value: 'EventRegistration',
      path: `${scope}/analytics/dashboard/leadCapture/EventRegistration`,
      emailDisabled: false,
      permissionKey: 'RegistrantList',
    },
    {
      label: 'Renewal Report',
      value: 'Renewal',
      path: `${scope}/analytics/dashboard/renewalReport`,
      emailDisabled: false,
      permissionKey: 'RenewalReport',
    },
    {
      label: 'In App Feedback',
      value: 'InAppFeedbackReport',
      path: `${scope}/analytics/dashboard/inAppFeedbackReport`,
      emailDisabled: false,
      permissionKey: 'InAppFeedback',
    },
    {
      label: 'Move out Inspection',
      value: 'MoveOutReport',
      path: `${scope}/analytics/dashboard/moveOutReport`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'MoveOutInspection',
    },
    {
      label: 'Equipment Dashboard',
      value: 'EquipmentDashboard',
      hideTitle: true,
      path: `${scope}/analytics/dashboard/equipmentDashboard`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'EquipmentLookup',
    },
    {
      label: 'Equipment Dashboard',
      value: 'GlobalEquipmentDashboard',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalEquipmentDashboard`,
      showEmail: false,
    },
    {
      label: 'Equipment Lookup',
      value: 'EquipmentLookup',
      path: `${scope}/analytics/dashboard/equipmentLookup`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'EquipmentLookup',
    },
    {
      label: 'Equipment Trend',
      value: 'EquipmentTrend',
      path: `${scope}/analytics/dashboard/equipmentTrend`,
      emailDisabled: false,
      showEmail: false,
      permissionKey: 'EquipmentTrend',
    },
    {
      label: 'Master Notepad',
      value: 'MasterNotepad',
      puppeteerUrl: 'pdfProofReport',
      // path: `${scope}/analytics/dashboard/masterNotepad`,
      path: `${scope}/masterNotepadDashboard`,
      permissionKey: 'MasterNotepad',
    },
    {
      label: 'Staff Check In Report',
      value: 'StaffCheckIn',
      path: `${scope}/analytics/dashboard/staffCheckIn`,
      permissionKey: 'StaffCheckInReport',
    },
    {
      label: 'Resident Log',
      value: 'ResidentLog',
      path: `${scope}/analytics/dashboard/residentLog`,
      permissionKey: 'ResidentLog',
    },
    {
      label: 'Guest Log',
      value: 'GuestLog',
      showEmail: false,
      path: `${scope}/analytics/dashboard/guestLog`,
      permissionKey: 'GuestLog',
    },
    {
      label: 'Comprehensive Feedback Report',
      value: 'ComprehensiveFeedbackReport',
      showEmail: false,
      path: `${scope}/analytics/dashboard/comprehensiveFeedback`,
      permissionKey: 'ComprehensiveFeedbackReport',
    },
    {
      label: 'Team Performance',
      value: 'TeamPerformance',
      hideTitle: true,
      showEmail: true,
      emailInline: true,
      puppeteerUrl: 'pdfTeamPerformance',
      path: `${scope}/analytics/dashboard/teamPerformance`,
      permissionKey: 'TeamPerformance',
    },
    {
      label: 'Team Performance',
      value: 'GlobalTeamPerformance',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalTeamPerformance`,
      showEmail: false,
    },
    {
      label: 'Work Order Dashboard',
      value: 'WorkOrderDashboard',
      // path:   '/analytics/dashboard/workOrder',
      path: `${scope}/workOrderDashboard`,
      showEmail: false,
      permissionKey: 'WorkOrderDashboard',
    },
    {
      // The Work Order Intelligence report (3310) surfaced as an in-app tab
      // (nav reorg §8); the page embeds the same report the render service prints.
      label: 'Work Order Intelligence',
      value: 'WorkOrderIntelligence',
      // Dropped from the Work Orders tab strip (2026-09): the dashboard's own Intelligence
      // button opens the report; the page stays reachable by URL.
      hidden: true,
      path: `${scope}/workOrderIntelligence`,
      showEmail: false,
      permissionKey: 'WorkOrderDashboard',
    },
    {
      label: 'Work Order Dashboard',
      value: 'GlobalWorkOrderDashboard',
      // path:   '/analytics/dashboard/workOrder',
      path: `${portfolioScope}/workOrderDashboardGlobal`,
      showEmail: false,
      permissionKey: 'WorkOrderDashboard',
    },
    {
      label: 'Accounting Dashboard',
      value: 'AccountingDashboard',
      path: `${scope}/accountingDashboard`,
      showEmail: false,
    },
    {
      label: 'Accounting Dashboard',
      value: 'GlobalAccountingDashboard',
      hideTitle: true,
      path: `${portfolioScope}/accountingDashboardGlobal`,
      showEmail: false,
    },
    {
      label: 'Insurance Dashboard',
      value: 'InsuranceDashboard',
      showEmail: false,
      // Lives under the COI nav item's tab shell (nav reorg).
      path: `${scope}/coi/insuranceDashboard`,
      permissionKey: 'InsuranceDashboard',
    },
    {
      label: 'Lease Intelligence',
      value: 'LeaseAbstraction',
      showEmail: false,
      // Lives under the Asset nav item's tab shell (nav reorg).
      path: `${scope}/asset/lease`,
    },
    {
      label: 'Roof Intelligence',
      value: 'RoofAbstraction',
      showEmail: false,
      // Lives under the Asset nav item's tab shell (nav reorg).
      path: `${scope}/asset/roof`,
    },
    {
      label: 'Compliance',
      value: 'ComplianceRecords',
      showEmail: false,
      // Lives under the Asset nav item's tab shell (nav reorg).
      path: `${scope}/asset/compliance`,
    },
    {
      label: 'Equipment Data Integrity Dashboard',
      value: 'EquipmentDataIntegrity',
      path: `${scope}/analytics/dashboard/dataIntegrityDashboard`,
      showEmail: false,
      permissionKey: 'EquipmentDataIntegrityDashboard',
    },
    {
      label: 'Deficiency Tracker',
      value: 'DeficiencyTracker',
      path: `${scope}/deficiencyTracker`,
      showEmail: false,
    },
    {
      label: 'Deficiency Tracker',
      value: 'DeficiencyTrackerGlobal',
      path: `${portfolioScope}/deficiencyTrackerGlobal`,
      showEmail: false,
    },
    {
      label: 'Collection Dashboard',
      value: 'collectionDashboard',
      path: `${scope}/collectionDashboard`,
      showEmail: true,
    },
    {
      label: 'Collection Dashboard',
      value: 'CollectionDashboardGlobal',
      path: `${portfolioScope}/collectionDashboardGlobal`,
      showEmail: false,
    },
    {
      label: 'Feedback On Service Request',
      value: 'CustomerFeedbackOnSR',
      path: `${scope}/analytics/dashboard/customerFeedbackOnSR`,
      puppeteerUrl: 'pdfCustomerFeedbackOnSR',
    },
    {
      label: 'Capital Planning',
      value: 'CapitalPlanning',
      // Lives under the Asset nav item's tab shell (nav reorg).
      path: `${scope}/asset/capitalPlanning`,
      showEmail: false,
    },
    {
      label: 'Capital Planning',
      value: 'GlobalCapitalPlanning',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalCapitalPlanning`,
      showEmail: false,
    },
    {
      label: 'PM Dashboard',
      value: 'GlobalPMCompliance',
      hideTitle: true,
      path: `${portfolioScope}/portfolioAnalytics/globalDashboard/globalPMCompliance`,
      showEmail: false,
    },
  ]
    .sort((a, b) => a.label.localeCompare(b.label))
    // Disabled dashboards stay in the registry (subscription/report-type
    // lookups by value keep working) but hidden from every picker.
    .map((v) => (DISABLED_DASHBOARD_VALUES.has(v.value) ? { ...v, hidden: true } : v))

export type ReportOption = ReturnType<typeof getTableList>[number] & {
  hidden?: boolean
  urlDateRange?: boolean
  showEmail?: boolean
  // Redesigned dashboards carry their own white shell, so the category header above
  // them drops its title and keeps only the tab strip (and any actions).
  hideTitle?: boolean
  // The dashboard draws the ?from/&to date pill in its own filter row (under the cards),
  // so the category header must not draw a second one.
  dateRangeInline?: boolean
  // The dashboard draws the EMAIL action in its own filter row (AnalyticsEmailButton).
  emailInline?: boolean
  emailDisabled?: boolean
  emailSendUrl?: boolean
  puppeteerUrl?: string
  permissionKey?: string
}

/**
 * Building-scope report options: live (not hidden), non-portfolio, permitted,
 * belonging to an intelligence module the portfolio has, ordered by
 * ANALYTICS_CATEGORIES so pickers group the same way the sidebar does. The
 * shell, the category header, and the sidebar all derive from this.
 *
 * `features` is the session's EnabledFeatureFlags (see useEnabledFeatures).
 * Omitting it leaves every module enabled, which is what non-gated callers want.
 */
export const getBuildingReports = (
  scope: string,
  permissions: Record<string, boolean>,
  features?: Record<string, boolean>,
): ReportOption[] =>
  (getTableList(scope) as ReportOption[])
    .filter((option) => !option.hidden && !option.value.includes('Global'))
    .filter((option) => !option.permissionKey || permissions?.[option.permissionKey])
    .filter((option) => {
      const feature = DASHBOARD_FEATURE[option.value]
      return !feature || !features || !!features[feature]
    })
    // Category order groups the options (they stay label-sorted within a
    // category — the base list is label-sorted and the sort is stable).
    .sort((a, b) => categoryRank(a.value) - categoryRank(b.value))

/**
 * Portfolio-scope report options — the `Global*` twins under Portfolio
 * Analytics — filtered and ordered exactly like getBuildingReports, so the
 * portfolio sidebar categories and tab strip derive the same way.
 */
export const getPortfolioReports = (
  scope: string,
  permissions: Record<string, boolean>,
  features?: Record<string, boolean>,
): ReportOption[] =>
  (getTableList('', scope) as ReportOption[])
    .filter((option) => !option.hidden && option.value.includes('Global'))
    .filter((option) => !option.permissionKey || permissions?.[option.permissionKey])
    .filter((option) => {
      const feature = DASHBOARD_FEATURE[option.value]
      return !feature || !features || !!features[feature]
    })
    .sort((a, b) => categoryRank(a.value) - categoryRank(b.value))

/**
 * The category a report belongs to, plus that category's permitted siblings.
 * `reports` is one scope's list, so the shared category yields only that
 * scope's tabs.
 */
export const getCategoryTabs = (selected: ReportOption | undefined, reports: ReportOption[]) => {
  const category = selected ? categoryOfValue(selected.value) : undefined
  if (!category) return { category, tabs: selected ? [selected] : [] }
  const tabs = categoryValues(category)
    .map((value) => reports.find((option) => option.value === value))
    .filter(Boolean) as ReportOption[]
  return { category, tabs }
}
